/**
 * Variant price & quantity handler
 * - Shows/hides pre-calculated price containers based on selected variant
 * - Adjusts quantity selector step/min/max/value based on variant UOM conversion metafield and inventory
 */
(function() {
  'use strict';

  const debug = true;

  const quantityControl = {
    initialized: false,
    field: null,
    input: null,
    plus: null,
    minus: null,
    minusWrapper: null,
    plusWrapper: null,
    defaultStep: 1,
    defaultMax: null,
    currentMax: null,
  };

  function log(...args) {
    if (debug) {
      console.log('[Variant Price Switcher]', ...args);
    }
  }

  function getDecimalPlaces(value) {
    const stringValue = value?.toString() || '';
    if (stringValue.includes('.')) {
      return stringValue.split('.')[1].length;
    }
    return 0;
  }

  function toNumber(value, fallback) {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }

    const numberValue = Number(value);
    if (Number.isFinite(numberValue)) {
      return numberValue;
    }

    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function formatQuantity(value, step) {
    const decimals = Math.max(getDecimalPlaces(step), 0);
    if (decimals > 0) {
      return Number(value).toFixed(decimals);
    }
    return Math.round(value).toString();
  }

  function normalizeValue(value, step, max, mode = 'ceil') {
    const numericStep = Math.max(toNumber(step, 1), 0.000001);
    let candidate = toNumber(value, numericStep);

    if (candidate < numericStep) {
      candidate = numericStep;
    }

    const precision = Math.max(getDecimalPlaces(candidate), getDecimalPlaces(numericStep));
    const factor = Math.pow(10, precision);
    const scaledValue = Math.round(candidate * factor);
    const scaledStep = Math.round(numericStep * factor);
    const remainder = scaledValue % scaledStep;

    if (remainder !== 0) {
      if (mode === 'floor') {
        candidate = (scaledValue - remainder) / factor;
        if (candidate < numericStep) {
          candidate = numericStep;
        }
      } else {
        candidate = (scaledValue - remainder + scaledStep) / factor;
      }
    }

    if (max !== null && max !== undefined) {
      const numericMax = toNumber(max, null);
      if (numericMax !== null && numericMax >= numericStep) {
        if (candidate > numericMax) {
          const maxPrecision = Math.max(precision, getDecimalPlaces(numericMax));
          const maxFactor = Math.pow(10, maxPrecision);
          const scaledMax = Math.round(numericMax * maxFactor);
          const scaledStepAdjusted = Math.round(numericStep * maxFactor);
          const multiples = Math.floor(scaledMax / scaledStepAdjusted) * scaledStepAdjusted;

          if (multiples >= scaledStepAdjusted) {
            candidate = multiples / maxFactor;
          } else {
            candidate = numericMax;
          }
        }
      }
    }

    return candidate;
  }

  function getCurrentStep() {
    if (!quantityControl.initialized) {
      return 1;
    }
    return toNumber(quantityControl.input.dataset.currentStep, quantityControl.defaultStep);
  }

  function getCurrentMax() {
    if (!quantityControl.initialized) {
      return null;
    }
    const datasetValue = quantityControl.input.dataset.currentMax;
    const numeric = toNumber(datasetValue, null);
    return numeric !== null && numeric > 0 ? numeric : null;
  }

  function updateAriaLabels(step) {
    const formattedStep = formatQuantity(step, step);
    if (quantityControl.plus) {
      const labelIncrease = window.theme?.strings?.increaseQuantity
        ? window.theme.strings.increaseQuantity.replace('{{ increment }}', formattedStep)
        : `Increase quantity by ${formattedStep}`;
      quantityControl.plus.setAttribute('aria-label', labelIncrease);
    }
    if (quantityControl.minus) {
      const labelDecrease = window.theme?.strings?.decreaseQuantity
        ? window.theme.strings.decreaseQuantity.replace('{{ increment }}', formattedStep)
        : `Decrease quantity by ${formattedStep}`;
      quantityControl.minus.setAttribute('aria-label', labelDecrease);
    }
  }

  function updateButtonStates() {
    if (!quantityControl.initialized) {
      return;
    }

    const step = getCurrentStep();
    const max = getCurrentMax();
    const currentValue = toNumber(quantityControl.input.value, step);

    const epsilon = 0.000001;
    const nextValue = currentValue + step;

    const shouldDisableMinus = currentValue <= step + epsilon;
    const shouldDisablePlus =
      max !== null && ((currentValue >= max - epsilon) || (nextValue > max + epsilon));

    if (quantityControl.minusWrapper) {
      quantityControl.minusWrapper.classList.toggle(
        'quantity-selector__button-wrapper--disabled',
        shouldDisableMinus
      );
    }
    if (quantityControl.plusWrapper) {
      quantityControl.plusWrapper.classList.toggle(
        'quantity-selector__button-wrapper--disabled',
        shouldDisablePlus
      );
    }

    if (quantityControl.minus) {
      if (shouldDisableMinus) {
        quantityControl.minus.setAttribute('disabled', 'disabled');
      } else {
        quantityControl.minus.removeAttribute('disabled');
      }
    }
    if (quantityControl.plus) {
      if (shouldDisablePlus) {
        quantityControl.plus.setAttribute('disabled', 'disabled');
      } else {
        quantityControl.plus.removeAttribute('disabled');
      }
    }
  }

  function triggerQuantityChange() {
    if (!quantityControl.initialized) {
      return;
    }
    const changeEvent = new Event('change', { bubbles: true });
    const inputEvent = new Event('input', { bubbles: true });
    quantityControl.input.dispatchEvent(inputEvent);
    quantityControl.input.dispatchEvent(changeEvent);
  }

  function setInputValue(value, step, options = {}) {
    if (!quantityControl.initialized) {
      return;
    }
    const mode = options.mode || 'ceil';
    const dispatch = options.dispatchChange !== false;
    const max = getCurrentMax();
    const normalized = normalizeValue(value, step, max, mode);

    quantityControl.input.value = formatQuantity(normalized, step);
    updateButtonStates();

    if (dispatch) {
      triggerQuantityChange();
    }
  }

  function onPlusClick(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const step = getCurrentStep();
    const currentValue = toNumber(quantityControl.input.value, step);
    const newValue = currentValue + step;
    setInputValue(newValue, step, { mode: 'ceil' });
  }

  function onMinusClick(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const step = getCurrentStep();
    const currentValue = toNumber(quantityControl.input.value, step);
    const newValue = currentValue - step;
    setInputValue(newValue, step, { mode: 'floor' });
  }

  function onQuantityInputChange(event) {
    // Don't prevent default - let the browser handle the change event normally
    // Only normalize the value, don't prevent the default behavior
    const step = getCurrentStep();
    const currentValue = toNumber(event.target.value, step);
    const max = getCurrentMax();
    
    // Only normalize if the value is invalid (not a multiple of step or out of bounds)
    // If the value is valid, preserve it
    const normalized = normalizeValue(currentValue, step, max, 'ceil');
    
    // Only update if the normalized value is different from what the user typed
    // This allows users to type valid values without interference
    if (Math.abs(normalized - currentValue) > 0.000001) {
      quantityControl.input.value = formatQuantity(normalized, step);
      updateButtonStates();
    } else {
      // Value is valid, just update button states
      updateButtonStates();
    }
  }

  function onQuantityInputBlur(event) {
    // Normalize value when user leaves the input field
    const step = getCurrentStep();
    setInputValue(event.target.value, step, { mode: 'ceil', dispatchChange: false });
  }

  function applyQuantitySettings(step, max, options = {}) {
    if (!quantityControl.initialized) {
      return;
    }

    const normalizedStep = Math.max(toNumber(step, quantityControl.defaultStep), 0.000001);
    const normalizedMax = max !== null && max !== undefined ? toNumber(max, null) : null;

    quantityControl.input.dataset.currentStep = normalizedStep;
    quantityControl.input.dataset.incrementValue = normalizedStep;
    quantityControl.input.setAttribute('data-increment-value', normalizedStep);
      quantityControl.input.setAttribute('step', formatQuantity(normalizedStep, normalizedStep));
    quantityControl.input.min = formatQuantity(normalizedStep, normalizedStep);

    quantityControl.currentMax = normalizedMax !== null && normalizedMax > 0 ? normalizedMax : null;
    if (quantityControl.currentMax !== null) {
      quantityControl.input.max = formatQuantity(quantityControl.currentMax, normalizedStep);
      quantityControl.input.dataset.currentMax = quantityControl.currentMax;
    } else {
      quantityControl.input.removeAttribute('max');
      quantityControl.input.dataset.currentMax = '';
    }

    updateAriaLabels(normalizedStep);

    if (options.updateValue !== false) {
      const newValue = options.resetToStep ? normalizedStep : (quantityControl.input.value || normalizedStep);
      setInputValue(newValue, normalizedStep, { mode: 'ceil', dispatchChange: options.dispatchChange });
    } else {
      updateButtonStates();
    }
  }

  function updateLinePropertiesForVariant(container) {
    const salesInput = document.querySelector('[data-uom-sales-input]');
    const conversionsInput = document.querySelector('[data-uom-conversions-input]');

    if (!salesInput && !conversionsInput) {
      return;
    }

    const salesValue = container?.dataset?.uomSales || '';
    const conversionsValue = container?.dataset?.uomStep || '';

    if (salesInput) {
      if (salesValue) {
        salesInput.value = salesValue;
        salesInput.removeAttribute('disabled');
      } else {
        salesInput.value = '';
        salesInput.setAttribute('disabled', 'disabled');
      }
    }

    if (conversionsInput) {
      if (conversionsValue) {
        conversionsInput.value = conversionsValue;
        conversionsInput.removeAttribute('disabled');
      } else {
        conversionsInput.value = '';
        conversionsInput.setAttribute('disabled', 'disabled');
      }
    }
  }

  function applyUomAvailabilityRules() {
    // Placeholder function for UoM availability rules
    // This function can be extended in the future if needed
    // Currently, availability is handled by updateQuantityForVariant()
    log('Applying UoM availability rules');
  }

  function updateQuantityForVariant(variantId) {
    if (!quantityControl.initialized) {
      log('Quantity control not initialized, skipping update');
      return;
    }

    if (!variantId) {
      log('No variant ID provided, skipping update');
      return;
    }

    // Ensure variantId is a string for the query selector
    const variantIdStr = String(variantId);
    const container = document.querySelector(`[data-variant-id="${variantIdStr}"]`);
    
    if (!container) {
      log('Container not found for variant:', variantIdStr);
      return;
    }

    const stepAttr = container?.dataset?.uomStep;
    const stockAttr = container?.dataset?.stock;
    const inventoryPolicy = container?.dataset?.inventoryPolicy;

    const step = stepAttr && stepAttr.toString().trim() !== '' ? toNumber(stepAttr, quantityControl.defaultStep) : quantityControl.defaultStep;

    log('Updating quantity for variant:', variantIdStr, 'with step:', step);

    let max = null;
    if (inventoryPolicy === 'deny') {
      const stock = toNumber(stockAttr, null);
      if (stock !== null && stock >= step) {
        max = stock;
      } else if (stock !== null && stock > 0) {
        max = stock;
      }
    }

    applyQuantitySettings(step, max, { updateValue: true, resetToStep: true });
    updateLinePropertiesForVariant(container);
  }

  function initQuantityControl() {
    const field = document.querySelector('[data-quantity-selector]');
    const input = field?.querySelector('[data-quantity-input]');
    const plus = field?.querySelector('[data-quantity-plus]');
    const minus = field?.querySelector('[data-quantity-minus]');
    const minusWrapper = field?.querySelector('[data-button-wrapper-minus]');
    const plusWrapper = field?.querySelector('[data-button-wrapper-plus]');

    if (!field || !input || !plus || !minus) {
      return;
    }

    if (quantityControl.initialized) {
      return;
    }

    quantityControl.field = field;
    quantityControl.input = input;
    quantityControl.plus = plus;
    quantityControl.minus = minus;
    quantityControl.minusWrapper = minusWrapper;
    quantityControl.plusWrapper = plusWrapper;
    quantityControl.defaultStep = Math.max(toNumber(input.dataset.defaultStep, toNumber(input.getAttribute('step'), 1)), 0.000001);

    const defaultMax = toNumber(input.dataset.defaultMax, null);
    quantityControl.defaultMax = defaultMax !== null && defaultMax > 0 ? defaultMax : null;
    const currentMax = toNumber(input.dataset.currentMax, quantityControl.defaultMax);
    quantityControl.currentMax = currentMax !== null && currentMax > 0 ? currentMax : null;

    quantityControl.initialized = true;

    plus.addEventListener('click', onPlusClick, true);
    minus.addEventListener('click', onMinusClick, true);
    input.addEventListener('change', onQuantityInputChange);
    input.addEventListener('blur', onQuantityInputBlur);

    const initialStep = Math.max(toNumber(input.dataset.currentStep, quantityControl.defaultStep), 0.000001);
    applyQuantitySettings(initialStep, quantityControl.currentMax, { updateValue: true, dispatchChange: false });
  }

  function showVariantPrice(variantId) {
    log('Switching to variant:', variantId);

    const allContainers = document.querySelectorAll('.variant-price-container');
    allContainers.forEach((container) => {
      container.style.display = 'none';
    });

    const targetContainer = document.querySelector(`[data-variant-id="${variantId}"]`);
    if (targetContainer) {
      targetContainer.style.display = 'block';
      log('Showed container for variant:', variantId);

      const priceData = targetContainer.querySelector('.variant-price-data');
      if (priceData) {
        try {
          const data = JSON.parse(priceData.textContent);
          log('Price data:', data);
        } catch (e) {
          log('Error parsing price data:', e);
        }
      }

      updateQuantityForVariant(variantId);
      applyUomAvailabilityRules();
    } else {
      log('No container found for variant:', variantId);
    }
  }

  function handleVariantChange(event) {
    log('Variant change detected:', event.detail);

    const variant = event.detail?.variant;
    if (variant && variant.id) {
      showVariantPrice(variant.id);
    }
  }

  function handleFormChange(event) {
    const target = event.target;
    if (target.name === 'id' || target.name?.includes('variant')) {
      const variantId = target.value;
      if (variantId && variantId !== 'not-selected') {
        log('Form change detected:', {
          name: target.name,
          value: variantId,
        });
        showVariantPrice(variantId);
      }
    }
  }

  function handleFormSubmit(event) {
    // Ensure the quantity value is preserved and valid when submitting the form
    // This runs BEFORE the form is serialized, so the correct value will be sent
    if (!quantityControl.initialized || !quantityControl.input) {
      return;
    }

    const step = getCurrentStep();
    const currentValue = toNumber(quantityControl.input.value, step);
    const max = getCurrentMax();

    // Only validate and normalize if the value is completely invalid
    // If it's a valid number within bounds, preserve it
    if (currentValue < step) {
      // Value is too small, set to minimum
      quantityControl.input.value = formatQuantity(step, step);
      log('Form submit - value too small, set to step:', step);
    } else if (max !== null && currentValue > max) {
      // Value exceeds max, set to max
      const normalizedMax = normalizeValue(max, step, max, 'floor');
      quantityControl.input.value = formatQuantity(normalizedMax, step);
      log('Form submit - value exceeds max, set to max:', normalizedMax);
    } else {
      // Value is valid, ensure it's formatted correctly but preserve the user's input
      // Only round to appropriate decimal places if needed
      const formattedValue = formatQuantity(currentValue, step);
      quantityControl.input.value = formattedValue;
      log('Form submit - preserving quantity value:', formattedValue);
    }

    // Don't prevent default - let the normal AddToCartFlyout flow continue
    // The value is now correct in the input, so serializeArray() will get the right value
  }

  function init() {
    log('Initializing variant price switcher...');

    document.addEventListener('variant-change', handleVariantChange);
    window.addEventListener('variant-change', handleVariantChange);
    document.body.addEventListener('variant-change', handleVariantChange);

    const originalDispatchEvent = EventTarget.prototype.dispatchEvent;
    EventTarget.prototype.dispatchEvent = function(event) {
      if (event.type === 'variant-change') {
        log('🎯 Intercepted variant-change event:', {
          target: this,
          detail: event.detail,
          timestamp: new Date().toISOString(),
        });
        handleVariantChange(event);
      }
      return originalDispatchEvent.call(this, event);
    };

    document.addEventListener('change', handleFormChange);

    initQuantityControl();

    // Add submit listener to preserve quantity value
    // Use capture phase to ensure it runs BEFORE other submit handlers
    const productForm = document.querySelector('[data-product-form]');
    if (productForm) {
      productForm.addEventListener('submit', handleFormSubmit, true); // true = capture phase
      log('Added form submit listener to preserve quantity (capture phase)');
    } else {
      // Fallback: try to find the form by other means
      const quantityInput = document.querySelector('[data-quantity-input]');
      if (quantityInput) {
        const form = quantityInput.closest('form');
        if (form) {
          form.addEventListener('submit', handleFormSubmit, true);
          log('Added form submit listener via quantity input (capture phase)');
        }
      }
    }

    const initialContainer = document.querySelector('.variant-price-container[style*="block"]');
    if (initialContainer) {
      const variantId = initialContainer.dataset.variantId;
      log('Initial variant detected:', variantId);
      updateQuantityForVariant(variantId);
    }

    applyUomAvailabilityRules();

    log('Variant price switcher ready');
  }

  window.VariantPriceSwitcher = {
    showVariantPrice,
    debug: () => {
      const containers = document.querySelectorAll('.variant-price-container');
      log('Found containers:', containers.length);
      containers.forEach((container, index) => {
        log(`Container ${index}:`, {
          variantId: container.dataset.variantId,
          visible: container.style.display !== 'none',
          element: container,
        });
      });
    },
    triggerVariantChange: (variantId) => {
      log('🚀 Manually triggering variant change:', variantId);
      const event = new CustomEvent('variant-change', {
        detail: {
          variant: { id: variantId },
        },
      });
      document.dispatchEvent(event);
    },
    getCurrentVariant: () => {
      const visibleContainer = document.querySelector('.variant-price-container[style*="block"]');
      return visibleContainer ? visibleContainer.dataset.variantId : null;
    },
    updateQuantityForVariant,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    setTimeout(init, 100);
  }

  log('Variant price switcher loaded');
})();
